// Topbar Feedback button, on every page (UAT). One sentence or one chip is
// enough: the page, the person, and anything they point at are captured for
// them. "Your feedback" lists only what this person sent. Saved server-side
// (utils/feedback.ts); the dev-team email is built for display but not sent.
import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { formatFeedbackTime, loadFeedback, saveFeedback, type FeedbackEntry } from '../../utils/feedback';
import { chipClass } from './QuickCheck';

const KINDS = ['Not how we work', "Something's wrong", 'Idea', 'I like this'];

// Climb to the nearest thing a person would call "that": a button, a row, a field.
function pickable(el: Element | null): HTMLElement | null {
  if (!el || el.closest('[data-feedback-ui]')) return null;
  return (el.closest('button, a, input, textarea, select, th, tr, li, h1, h2, h3, [data-tour]') as HTMLElement | null) ?? (el as HTMLElement);
}

function describeElement(el: HTMLElement): string {
  const raw = el.getAttribute('aria-label') || el.getAttribute('title') || (el as HTMLInputElement).placeholder || el.innerText || '';
  const text = raw.trim().split('\n')[0].replace(/\s+/g, ' ');
  const tag = el.tagName.toLowerCase();
  const kind = tag === 'button' ? 'button'
    : tag === 'a' ? 'link'
    : ['input', 'textarea', 'select'].includes(tag) ? 'field'
    : tag === 'th' ? 'column'
    : tag === 'tr' ? 'row'
    : /^h[1-3]$/.test(tag) ? 'heading'
    : 'section';
  return text ? `"${text}" ${kind}` : `Unlabelled ${kind}`;
}

function PickOverlay({ onPick, onCancel }: { onPick: (label: string) => void; onCancel: () => void }) {
  const [box, setBox] = useState<DOMRect | null>(null);
  const target = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const move = (e: MouseEvent) => {
      const el = pickable(document.elementFromPoint(e.clientX, e.clientY));
      target.current = el;
      setBox(el ? el.getBoundingClientRect() : null);
    };
    // Swallow the click so pointing at a button doesn't press it.
    const block = (e: Event) => {
      if ((e.target as Element).closest?.('[data-feedback-ui]')) return;
      e.preventDefault();
      e.stopPropagation();
    };
    const click = (e: MouseEvent) => {
      if ((e.target as Element).closest?.('[data-feedback-ui]')) return;
      block(e);
      if (target.current) onPick(describeElement(target.current));
    };
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    const prevCursor = document.body.style.cursor;
    document.body.style.cursor = 'crosshair';
    window.addEventListener('mousemove', move, true);
    window.addEventListener('mousedown', block, true);
    window.addEventListener('click', click, true);
    window.addEventListener('keydown', key);
    return () => {
      document.body.style.cursor = prevCursor;
      window.removeEventListener('mousemove', move, true);
      window.removeEventListener('mousedown', block, true);
      window.removeEventListener('click', click, true);
      window.removeEventListener('keydown', key);
    };
  }, [onPick, onCancel]);

  return (
    <>
      {box && (
        <div
          aria-hidden
          className="fixed z-[600] pointer-events-none rounded-lg border-2 border-teal-500 bg-teal-500/10 transition-all duration-75"
          style={{ top: box.top - 3, left: box.left - 3, width: box.width + 6, height: box.height + 6 }}
        />
      )}
      <div data-feedback-ui className="fixed z-[601] top-[68px] left-1/2 -translate-x-1/2 flex items-center gap-3 px-4 py-2.5 rounded-xl bg-white border border-trim shadow-modal">
        <span className="text-sm text-ink">Click the part of the page your feedback is about.</span>
        <button onClick={onCancel} className="text-xs font-semibold text-teal-600 hover:underline">Cancel</button>
      </div>
    </>
  );
}

export function FeedbackButton({ pageLabel }: { pageLabel: string }) {
  const { user } = useAuth();
  const [mode, setMode] = useState<'closed' | 'open' | 'picking'>('closed');
  const [tab, setTab] = useState<'send' | 'mine'>('send');
  const [kind, setKind] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [about, setAbout] = useState<string | null>(null);
  const [sent, setSent] = useState<FeedbackEntry | null>(null);
  const [mine, setMine] = useState<FeedbackEntry[]>([]);
  const ref = useRef<HTMLDivElement>(null);

  // Something picked on one page isn't what the next page's feedback is about.
  useEffect(() => { setAbout(null); }, [pageLabel]);

  useEffect(() => {
    if (mode === 'open') loadFeedback(user?.username).then(setMine);
  }, [mode, sent, user?.username]);

  useEffect(() => {
    if (mode !== 'open') return;
    const onDown = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setMode('closed'); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMode('closed'); };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [mode]);

  function toggle() {
    if (mode === 'open') { setMode('closed'); return; }
    setSent(null);
    setTab('send');
    setMode('open');
  }

  async function submit() {
    if (!user) return;
    const entry = await saveFeedback({ source: 'button', kind, note: note.trim(), page: pageLabel, about });
    setSent(entry);
    setKind(null);
    setNote('');
    setAbout(null);
  }

  const canSend = !!note.trim() || !!kind;

  return (
    <div className="relative" ref={ref} data-feedback-ui>
      <button
        onClick={toggle}
        className="h-8 px-3.5 rounded-full flex items-center gap-1.5 text-white bg-teal-500 border border-teal-400 hover:bg-teal-400 shadow-card transition-all"
        title="Tell the dev team what you think of this page"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M2.5 3.5a1 1 0 011-1h9a1 1 0 011 1v6.5a1 1 0 01-1 1H7l-3 2.5v-2.5h-.5a1 1 0 01-1-1V3.5z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
          <path d="M5.5 6h5M5.5 8.2h3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
        <span className="text-xs font-semibold">Give feedback</span>
      </button>

      {mode === 'picking' && (
        <PickOverlay
          onPick={label => { setAbout(label); setMode('open'); }}
          onCancel={() => setMode('open')}
        />
      )}

      {mode === 'open' && (
        <div className="absolute top-[calc(100%+8px)] right-0 w-[360px] bg-white border border-trim rounded-xl shadow-modal z-[470] overflow-hidden">
          <div className="flex border-b border-trim">
            {([['send', 'Send feedback'], ['mine', `Your feedback${mine.length ? ` (${mine.length})` : ''}`]] as const).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                className={`flex-1 px-4 py-2.5 text-xs font-semibold border-b-2 -mb-px ${tab === key ? 'border-teal-500 text-ink' : 'border-transparent text-muted hover:text-copy'}`}
              >
                {label}
              </button>
            ))}
          </div>

          {tab === 'send' && sent && (
            <div className="px-4 py-6 text-center">
              <div className="w-9 h-9 mx-auto rounded-full bg-tint flex items-center justify-center">
                <svg width="16" height="16" viewBox="0 0 14 14" fill="none"><path d="M3 7l2.5 2.5L11 4" stroke="#2D7575" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </div>
              <div className="text-sm font-semibold text-ink mt-2">Thanks. Your feedback was submitted.</div>
              <div className="flex justify-center gap-4 mt-4">
                <button onClick={() => setTab('mine')} className="text-xs font-semibold text-teal-600 hover:underline">See what you sent</button>
                <button onClick={() => setSent(null)} className="text-xs font-semibold text-copy hover:underline">Send more</button>
              </div>
            </div>
          )}

          {tab === 'send' && !sent && (
            <div className="px-4 py-3">
              <div className="flex items-center gap-1.5 text-xs text-muted min-w-0">
                <span className="flex-shrink-0">About</span>
                <span className="font-semibold text-ink truncate">{about ?? pageLabel}</span>
                {about && (
                  <button onClick={() => setAbout(null)} className="flex-shrink-0 text-muted hover:text-ink" title="Make it about the whole page">✕</button>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2.5">
                {KINDS.map(k => (
                  <button key={k} onClick={() => setKind(prev => (prev === k ? null : k))} className={chipClass(kind === k)}>{k}</button>
                ))}
              </div>
              <textarea
                autoFocus value={note} onChange={e => setNote(e.target.value)} rows={4}
                placeholder="What's on your mind about this page?"
                className="w-full mt-2.5 px-2.5 py-2 rounded-lg border border-trim text-sm text-ink outline-none focus:border-teal-400 bg-white resize-none"
              />
              <div className="flex items-center justify-between mt-2">
                <button onClick={() => setMode('picking')} className="flex items-center gap-1.5 text-xs font-semibold text-teal-600 hover:underline">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><circle cx="6" cy="6" r="4" stroke="currentColor" strokeWidth="1.3" /><path d="M6 0.5v2.5M6 9v2.5M0.5 6H3M9 6h2.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" /></svg>
                  Point at something
                </button>
                <button
                  onClick={submit} disabled={!canSend}
                  className="text-xs font-semibold px-3.5 py-1.5 rounded-lg text-white bg-teal-500 hover:bg-teal-600 disabled:opacity-40"
                >
                  Send
                </button>
              </div>
              <div className="text-xs text-muted mt-2.5">A chip or a sentence is plenty. We include the page you're on for you.</div>
            </div>
          )}

          {tab === 'mine' && (
            <div className="max-h-[60vh] overflow-y-auto">
              {mine.length === 0 && <div className="px-4 py-6 text-xs text-muted text-center">Nothing yet. Anything you send shows up here.</div>}
              {mine.map(e => (
                <div key={e.id} className="px-4 py-3 border-b border-trim last:border-b-0">
                  <div className="flex items-center gap-2">
                    {e.kind && <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-tint text-ink">{e.kind}</span>}
                    <span className="ml-auto text-xs text-muted">{formatFeedbackTime(e.at)}</span>
                  </div>
                  <div className="text-xs text-muted mt-1.5 truncate">{e.page.label}{e.about ? ` · ${e.about}` : ''}</div>
                  {e.reasons.length > 0 && <div className="text-xs text-copy mt-1">{e.reasons.join(', ')}</div>}
                  {e.note && <div className="text-sm text-ink mt-1 whitespace-pre-wrap">{e.note}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
